import { create } from 'zustand';
import qs from 'qs';
import { getUploadErrorMessage, validateFileSize } from '@/lib/upload';
import { fetchStrapiCollection } from '@/lib/strapi';
import { normalizeInsuranceName, dedupeByInsuranceName } from '@/lib/insuranceName';
import { normalizeExternalUrl } from '@/utils';
import {
  Provider,
  CreateProviderData,
  ProviderDegree,
  ProviderGender,
  ProviderLanguage,
  ProviderLanguageRecord,
  ProviderPatientType,
  ProviderSession,
  ProviderSpecialty,
  ProviderSpecialtyRecord,
  InsuranceRecord,
} from '@/types';

export type ProviderCategoryFilter =
  | 'all'
  | 'adhd'
  | 'adults'
  | 'anxiety'
  | 'bipolar-disorder'
  | 'couples'
  | 'depression'
  | 'eating-disorders'
  | 'families'
  | 'grief-loss'
  | 'lgbtqia'
  | 'ocd'
  | 'otr-l'
  | 'seniors-elderly'
  | 'sexual-trauma'
  | 'substance-use'
  | 'suicide-ideation'
  | 'adolescents-teens'
  | 'trauma-ptsd';

export const PROVIDER_CATEGORY_OPTIONS: { label: string; value: ProviderCategoryFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'ADHD', value: 'adhd' },
  { label: 'Adults', value: 'adults' },
  { label: 'Anxiety', value: 'anxiety' },
  { label: 'Bipolar Disorder', value: 'bipolar-disorder' },
  { label: 'Couples', value: 'couples' },
  { label: 'Depression', value: 'depression' },
  { label: 'Eating Disorders', value: 'eating-disorders' },
  { label: 'Families', value: 'families' },
  { label: 'Grief/Loss', value: 'grief-loss' },
  { label: 'LGBTQIA+', value: 'lgbtqia' },
  { label: 'OCD', value: 'ocd' },
  { label: 'Occ. Therapy (OT)', value: 'otr-l' },
  { label: 'Seniors/Elderly', value: 'seniors-elderly' },
  { label: 'Sexual Trauma', value: 'sexual-trauma' },
  { label: 'Substance Use', value: 'substance-use' },
  { label: 'Suicide Ideation', value: 'suicide-ideation' },
  { label: 'Teens/Adolescents', value: 'adolescents-teens' },
  { label: 'Trauma/PTSD', value: 'trauma-ptsd' },
];


interface ProviderState {
  providers: Provider[];
  // MWBI-803 — canonical taxonomy lists, fetched from the content types.
  // Modal dropdowns and the store's name→documentId resolver both read these.
  availableSpecialties: ProviderSpecialtyRecord[];
  availableLanguages: ProviderLanguageRecord[];
  availableInsurances: InsuranceRecord[];
  specialtiesLoaded: boolean;
  languagesLoaded: boolean;
  insurancesLoaded: boolean;
  selectedCategory: ProviderCategoryFilter;
  searchQuery: string;
  loading: boolean;
  error: string | null;
  fetchProviders: () => Promise<void>;
  fetchSpecialties: (force?: boolean) => Promise<void>;
  fetchLanguages: (force?: boolean) => Promise<void>;
  fetchInsurances: (force?: boolean) => Promise<void>;
  syncMyFavorites: (userId: number) => Promise<void>;
  fetchFavoriteState: (documentId: string, userId: number) => Promise<void>;
  toggleFavorite: (documentId: string, userId: number) => Promise<{ success: boolean; favorited?: boolean; error?: string }>;
  hasUserFavorited: (documentId: string, userId: number) => boolean;
  setSelectedCategory: (category: ProviderCategoryFilter) => void;
  setSearchQuery: (query: string) => void;
  getFilteredProviders: (userId?: number) => Provider[];
  createProvider: (
    data: CreateProviderData,
    imageFile?: File | null
  ) => Promise<{ success: boolean; provider?: Provider; error?: string }>;
  updateProvider: (
    documentId: string,
    data: Partial<CreateProviderData>,
    imageFile?: File | null
  ) => Promise<{ success: boolean; provider?: Provider; error?: string }>;
  deleteProvider: (documentId: string) => Promise<{ success: boolean; error?: string }>;
  fetchProvider: (documentId: string) => Promise<Provider | null>;
}

const baseUrl = '/api/strapi';

// MWBI-844 / MWBI-852 — single source of truth for the relation + media
// populate used when reading a single provider AND on the create/update write
// responses. Requesting it on the POST/PUT means the write response carries the
// fresh relations + photo back (read-after-write), so mapApiProviderToStore
// derives the updated specialties / languages / insurance / photo and the UI
// reflects the save immediately, without a page refresh.
const PROVIDER_DETAIL_POPULATE = {
  photo: true,
  user: true,
  providerSpecialties: { fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived'] },
  providerLanguages: { fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived'] },
  insurances: { fields: ['id', 'documentId', 'name'] },
} as const;

const SPECIALTY_FILTER_MAP: Partial<Record<ProviderCategoryFilter, ProviderSpecialty[]>> = {
  'anxiety': ['Anxiety'],
  'depression': ['Depression'],
  'trauma-ptsd': ['Trauma/PTSD'],
  'adhd': ['ADHD'],
  'bipolar-disorder': ['Bipolar Disorder'],
  'eating-disorders': ['Eating Disorders'],
  'substance-use': ['Substance Use', 'Substance Abuse'],
  'ocd': ['OCD'],
  'grief-loss': ['Grief/Loss'],
  'lgbtqia': ['LGBTQIA+', 'LGBTQ+'],
  'sexual-trauma': ['Sexual Trauma'],
  'suicide-ideation': ['Suicide Ideation'],
};

const PATIENT_FILTER_MAP: Partial<Record<ProviderCategoryFilter, ProviderPatientType>> = {
  'adolescents-teens': 'Adolescents',
  'seniors-elderly': 'Elderly',
  'adults': 'Adults',
  'couples': 'Couples',
  'families': 'Families',
};

// MWBI-884 — OTR/L (Occupational Therapy) is a credential, not a specialty
// or patient type, so it filters on the provider's degree. MWBI-903 removed
// the COTA/L filter option per the master taxonomy list; COTA/L remains a
// valid ProviderDegree value — providers with that credential are unaffected,
// they're just no longer filterable by it specifically.
const DEGREE_FILTER_MAP: Partial<Record<ProviderCategoryFilter, ProviderDegree>> = {
  'otr-l': 'OTR/L',
};

const filterProviderByCategory = (provider: Provider, category: ProviderCategoryFilter): boolean => {
  const specialties = SPECIALTY_FILTER_MAP[category];
  if (specialties) return specialties.some(s => provider.specialties.includes(s));
  const patientType = PATIENT_FILTER_MAP[category];
  if (patientType) return provider.patients.includes(patientType);
  const degree = DEGREE_FILTER_MAP[category];
  if (degree) {
    // Prefer the canonical MWBI-803 degreesList; fall back to the deprecated
    // single `degrees` column when the list is absent.
    const degrees = provider.degreesList ?? [provider.degrees];
    return degrees.includes(degree);
  }
  return true;
};

const PROVIDER_DEGREE_VALUES: ProviderDegree[] = ['MD', 'DO', 'PhD', 'PsyD', 'LCSW', 'LMFT', 'LPC', 'LCPC', 'NP', 'PA', 'RN', 'CRNA', 'OTR/L', 'COTA/L', 'Other'];
const PROVIDER_LANGUAGE_VALUES: ProviderLanguage[] = ['English', 'Spanish', 'French', 'Mandarin', 'Vietnamese', 'Korean', 'Other'];
const PROVIDER_SESSION_VALUES: ProviderSession[] = ['In Person', 'Virtual'];
const PROVIDER_SPECIALTY_VALUES: ProviderSpecialty[] = [
  'Anxiety',
  'Depression',
  'Trauma/PTSD',
  'ADHD',
  'Bipolar Disorder',
  'Eating Disorders',
  'Substance Abuse',
  'Substance Use',
  'OCD',
  'Grief/Loss',
  'Relationship Issues',
  'Family Therapy',
  'Child & Adolescent',
  'LGBTQ+',
  'LGBTQIA+',
  'Sexual Trauma',
  'Suicide Ideation',
  'Other',
];
const PROVIDER_PATIENT_VALUES: ProviderPatientType[] = ['Adults', 'Children', 'Adolescents', 'Elderly', 'Couples', 'Families'];

const isProviderDegree = (value: string): value is ProviderDegree => PROVIDER_DEGREE_VALUES.includes(value as ProviderDegree);
const isProviderLanguage = (value: string): value is ProviderLanguage => PROVIDER_LANGUAGE_VALUES.includes(value as ProviderLanguage);
const isProviderSession = (value: string): value is ProviderSession => PROVIDER_SESSION_VALUES.includes(value as ProviderSession);
const isProviderSpecialty = (value: string): value is ProviderSpecialty => PROVIDER_SPECIALTY_VALUES.includes(value as ProviderSpecialty);
const isProviderPatientType = (value: string): value is ProviderPatientType => PROVIDER_PATIENT_VALUES.includes(value as ProviderPatientType);

const normalizeProviderArray = <T extends string>(
  values: string[] | undefined,
  isValid: (value: string) => value is T,
  fallback: T[] = [],
): T[] => {
  if (!Array.isArray(values)) {
    return fallback;
  }

  const normalizedValues = values.filter(isValid);
  return normalizedValues.length > 0 ? normalizedValues : fallback;
};

const normalizeProviderDegree = (value: string, fallback: ProviderDegree = 'Other'): ProviderDegree => (
  isProviderDegree(value) ? value : fallback
);

// Maps stored English values to their Spanish equivalents so Spanish searches work.
const ES_SYNONYMS: Record<string, string> = {
  'Accepting New Patients': 'Aceptando Nuevos Pacientes',
  'Waitlist': 'Lista de Espera',
  'Anxiety': 'Ansiedad',
  'Depression': 'Depresión',
  'Trauma/PTSD': 'Trauma/TEPT',
  'ADHD': 'TDAH',
  'Bipolar Disorder': 'Trastorno Bipolar',
  'Eating Disorders': 'Trastornos Alimenticios',
  'Substance Abuse': 'Abuso de Sustancias',
  'Substance Use': 'Uso de Sustancias',
  'OCD': 'TOC',
  'Grief/Loss': 'Duelo/Pérdida',
  'Relationship Issues': 'Problemas de Pareja',
  'Family Therapy': 'Terapia Familiar',
  'Child & Adolescent': 'Niños y Adolescentes',
  'LGBTQIA+': 'LGBTQIA+',
  'Sexual Trauma': 'Trauma Sexual',
  'Suicide Ideation': 'Ideación Suicida',
  'Other': 'Otro',
  'Adults': 'Adultos',
  'Children': 'Niños',
  'Adolescents': 'Adolescentes',
  'Elderly': 'Ancianos',
  'Couples': 'Parejas',
  'Families': 'Familias',
  'English': 'Inglés',
  'Spanish': 'Español',
  'In Person': 'En Persona',
  'Virtual': 'Virtual',
  'male': 'masculino',
  'female': 'femenino',
};

const providerMatchesQuery = (provider: Provider, rawQuery: string): boolean => {
  const trimmed = rawQuery.trim().toLowerCase();
  if (!trimmed) return true;

  const words = trimmed.split(/\s+/).filter(Boolean);

  const buildSearchableParts = (): string[] => {
    const parts: string[] = [
      provider.providerName,
      provider.groupName || '',
      provider.degrees,
      ...(provider.degreesList || []),
      String(provider.yearsOfExperience),
      provider.location,
      provider.bookingLink || '',
      provider.sessionCost || '',
      provider.officeHours || '',
      provider.about || '',
      ...(provider.languages || []),
      ...(provider.sessions || []),
      ...(provider.specialties || []),
      ...(provider.patients || []),
      ...(provider.insurance || []),
      ...(provider.acceptingPatients ? ['Accepting New Patients'] : []),
      ...(provider.waitlist ? ['Waitlist'] : []),
      ...(provider.gender ? [provider.gender] : []),
    ];

    // Add Spanish equivalents for stored English values
    const withSynonyms = parts.flatMap((value) => {
      const synonym = ES_SYNONYMS[value];
      return synonym ? [value, synonym] : [value];
    });

    return withSynonyms;
  };

  const searchableParts = buildSearchableParts();
  const searchableText = searchableParts.join(' ').toLowerCase();

  // Any word from the query matching anywhere is enough to show the provider
  return words.some((word) => searchableText.includes(word));
};

interface StrapiProviderItem {
  id: number;
  documentId: string;
  providerName: string;
  groupName?: string;
  photo?: { url: string } | null;
  degrees: string;
  degreesList?: string[];
  yearsOfExperience: number;
  // MWBI-803 — relation fields populated alongside the legacy json fields
  // during the rollout window. After the bootstrap backfill runs, the
  // relations are the source of truth; the legacy fields stay populated
  // (private, never written by code) until the column is dropped.
  providerSpecialties?: ProviderSpecialtyRecord[];
  providerLanguages?: ProviderLanguageRecord[];
  insurances?: InsuranceRecord[];
  // Legacy json fields — may still be present on un-backfilled rows during
  // the transition. Renamed in Strapi to `legacy*`; the response exposes
  // them under the same name only when explicitly populated. Kept here so
  // the store can fall back gracefully if a relation hasn't been linked yet.
  legacySpecialties?: string[];
  legacyLanguages?: string[];
  legacyInsurance?: string[];
  sessions?: string[];
  acceptingPatients?: boolean;
  waitlist?: boolean;
  patients?: string[];
  location: string;
  phone?: string;
  email?: string;
  bookingLink?: string;
  sessionCost?: string;
  officeHours?: string;
  officeAddress?: string;
  about?: string;
  gender?: string;
  userId?: number;
  favorited_by?: { id: number }[];
  favoriteCount?: number;
  user?: { id: number } | null;
  createdAt?: string;
  updatedAt?: string;
}

type ProviderStoreGetter = () => ProviderState;

/**
 * MWBI-803 — fetch all three canonical taxonomy lists once if any are
 * unloaded. createProvider / updateProvider call this before resolving
 * name arrays to documentIds so the resolver has data to work with.
 */
async function ensureProviderTaxonomyListsLoaded(get: ProviderStoreGetter): Promise<void> {
  const state = get();
  const tasks: Promise<void>[] = [];
  if (!state.specialtiesLoaded) tasks.push(state.fetchSpecialties());
  if (!state.languagesLoaded) tasks.push(state.fetchLanguages());
  if (!state.insurancesLoaded) tasks.push(state.fetchInsurances());
  if (tasks.length > 0) await Promise.all(tasks);
}

const PROVIDER_LANGUAGE_LEGACY_NAME_ALIAS: Record<string, string> = {
  ASL: 'American Sign Language',
};
const PROVIDER_SPECIALTY_LEGACY_NAME_ALIAS: Record<string, string> = {
  'LGBTQ+': 'LGBTQIA+',
  'Substance Abuse': 'Substance Use',
};

/**
 * Convert the CreateProviderData shape (which still carries name-strings
 * for backwards compat with the existing modal UI) into a Strapi payload
 * that writes the new manyToMany relations. Legacy json fields are
 * intentionally dropped — the controller strips them anyway, but a clean
 * payload makes debugging easier.
 */
function buildProviderRelationPayload(
  data: Partial<CreateProviderData>,
  get: ProviderStoreGetter,
): { data: Record<string, unknown> } {
  const { availableSpecialties, availableLanguages, availableInsurances } = get();
  const specialtyDocIdByName = new Map(availableSpecialties.map((r) => [r.name, r.documentId]));
  const languageDocIdByName = new Map(availableLanguages.map((r) => [r.name, r.documentId]));
  const insuranceDocIdByName = new Map(availableInsurances.map((r) => [r.name, r.documentId]));

  const resolve = (
    names: string[] | undefined,
    aliasMap: Record<string, string>,
    docIdByName: Map<string, string>,
  ): string[] | undefined => {
    if (names === undefined) return undefined; // leave relation untouched on update
    if (names.length === 0) return []; // deliberate clear -> emit `set: []`
    const ids = names
      .map((n) => aliasMap[n] ?? n)
      .map((n) => docIdByName.get(n))
      .filter((id): id is string => Boolean(id));
    // MWBI-844 — if any selected name fails to resolve to a documentId, the
    // taxonomy list is almost certainly stale or unloaded, not a deliberate
    // removal. Emitting `set: ids` here would permanently drop the unresolved
    // members (whether all of them — a full wipe — or only some — a partial
    // wipe), and these taxonomy types are draftAndPublish:false, so there is no
    // draft buffer to recover from. Leave the live relation untouched instead.
    if (ids.length !== names.length) return undefined;
    return ids;
  };

  const payload: Record<string, unknown> = { ...data };
  // Strip legacy/derived string fields — relations are the SSOT.
  delete payload.specialties;
  delete payload.languages;
  delete payload.insurance;

  // MWBI — a provider-entered website like `www.wildgemtherapy.com` has no
  // scheme, so the browser resolves it relative to the current page and the
  // link breaks. Normalize on save (prepend https://, allowlist http/https).
  // Only touch the field when the caller actually provided a value, so an
  // update that omits bookingLink leaves the stored value untouched.
  if (typeof payload.bookingLink === 'string') {
    payload.bookingLink = normalizeExternalUrl(payload.bookingLink) ?? '';
  }

  const specialtyDocIds = resolve(data.specialties, PROVIDER_SPECIALTY_LEGACY_NAME_ALIAS, specialtyDocIdByName);
  if (specialtyDocIds !== undefined) {
    payload.providerSpecialties = { set: specialtyDocIds };
  }
  const languageDocIds = resolve(data.languages, PROVIDER_LANGUAGE_LEGACY_NAME_ALIAS, languageDocIdByName);
  if (languageDocIds !== undefined) {
    payload.providerLanguages = { set: languageDocIds };
  }
  const insuranceDocIds = resolve(data.insurance, {}, insuranceDocIdByName);
  if (insuranceDocIds !== undefined) {
    payload.insurances = { set: insuranceDocIds };
  }

  return { data: payload };
}

const mapApiProviderToStore = (item: StrapiProviderItem, existingProvider?: Provider): Provider => {
  const degrees = normalizeProviderDegree(item.degrees, existingProvider?.degrees ?? 'Other');
  const fallbackDegreesList = existingProvider?.degreesList?.length ? existingProvider.degreesList : [degrees];

  // MWBI-803 — prefer the relation records, fall back to the legacy json
  // for pre-backfill records. Derived string arrays keep the existing
  // search/filter code working without changes.
  const specialtyRecords = item.providerSpecialties ?? [];
  const languageRecords = item.providerLanguages ?? [];
  const insuranceRecords = item.insurances ?? [];

  const derivedSpecialtyNames = specialtyRecords.length > 0
    ? specialtyRecords.map((r) => r.name)
    : (item.legacySpecialties ?? []);
  const derivedLanguageNames = languageRecords.length > 0
    ? languageRecords.map((r) => r.name)
    : (item.legacyLanguages ?? []);
  const derivedInsuranceNames = insuranceRecords.length > 0
    ? insuranceRecords.map((r) => r.name)
    : (item.legacyInsurance ?? []);

  return {
    id: item.id,
    documentId: item.documentId,
    providerName: item.providerName,
    groupName: item.groupName,
    photo: item.photo ? { url: item.photo.url } : existingProvider?.photo ?? null,
    degrees,
    degreesList: normalizeProviderArray(item.degreesList, isProviderDegree, fallbackDegreesList),
    yearsOfExperience: item.yearsOfExperience,
    languages: normalizeProviderArray(derivedLanguageNames, isProviderLanguage, existingProvider?.languages ?? []),
    sessions: normalizeProviderArray(item.sessions, isProviderSession, existingProvider?.sessions ?? []),
    acceptingPatients: item.acceptingPatients ?? existingProvider?.acceptingPatients ?? true,
    waitlist: item.waitlist ?? existingProvider?.waitlist ?? false,
    specialties: normalizeProviderArray(derivedSpecialtyNames, isProviderSpecialty, existingProvider?.specialties ?? []),
    providerSpecialties: specialtyRecords.length > 0 ? specialtyRecords : existingProvider?.providerSpecialties,
    providerLanguages: languageRecords.length > 0 ? languageRecords : existingProvider?.providerLanguages,
    insurances: insuranceRecords.length > 0 ? insuranceRecords : existingProvider?.insurances,
    patients: normalizeProviderArray(item.patients, isProviderPatientType, existingProvider?.patients ?? []),
    location: item.location,
    phone: item.phone ?? existingProvider?.phone ?? '',
    email: item.email ?? existingProvider?.email,
    bookingLink: item.bookingLink,
    sessionCost: item.sessionCost,
    officeHours: item.officeHours,
    officeAddress: item.officeAddress ?? existingProvider?.officeAddress,
    insurance: derivedInsuranceNames.length > 0 ? derivedInsuranceNames : (existingProvider?.insurance ?? []),
    about: item.about,
    gender: (item.gender === 'male' || item.gender === 'female') ? item.gender as ProviderGender : existingProvider?.gender,
    favorited_by: item.favorited_by || existingProvider?.favorited_by || [],
    favoriteCount: typeof item.favoriteCount === 'number' ? item.favoriteCount : (existingProvider?.favoriteCount || 0),
    userId: item.userId ?? item.user?.id ?? existingProvider?.userId,
    createdAt: item.createdAt || existingProvider?.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || existingProvider?.updatedAt || new Date().toISOString(),
  };
};

export const useProviderStore = create<ProviderState>((set, get) => ({
  providers: [],
  availableSpecialties: [],
  availableLanguages: [],
  availableInsurances: [],
  specialtiesLoaded: false,
  languagesLoaded: false,
  insurancesLoaded: false,
  selectedCategory: 'all',
  searchQuery: '',
  loading: false,
  error: null,

  fetchSpecialties: async (force = false) => {
    if (!force && get().specialtiesLoaded) return;
    try {
      const rows = await fetchStrapiCollection<ProviderSpecialtyRecord>('provider-specialties', {
        fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived'],
        sort: ['order:asc', 'name:asc'],
      });
      set({ availableSpecialties: rows, specialtiesLoaded: true });
    } catch (error) {
      console.error('Error fetching provider specialties:', error);
    }
  },

  fetchLanguages: async (force = false) => {
    if (!force && get().languagesLoaded) return;
    try {
      const rows = await fetchStrapiCollection<ProviderLanguageRecord>('provider-languages', {
        fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived'],
        sort: ['order:asc', 'name:asc'],
      });
      set({ availableLanguages: rows, languagesLoaded: true });
    } catch (error) {
      console.error('Error fetching provider languages:', error);
    }
  },

  fetchInsurances: async (force = false) => {
    if (!force && get().insurancesLoaded) return;
    try {
      const rows = await fetchStrapiCollection<InsuranceRecord>('insurances', {
        fields: ['id', 'documentId', 'name'],
        // MWBI-844 — mirror insuranceStore: skip soft-deleted rows so a deleted
        // insurance can't shadow a live one, and normalize + dedupe names so the
        // resolver's name->documentId map keys match the canonical names the
        // edit form stores. Without this the resolver and the UI read different
        // spellings and a just-added insurance is silently dropped.
        filters: { deletedAt: { $null: true } },
        sort: ['name:asc'],
      });
      const availableInsurances = dedupeByInsuranceName(
        rows
          .map((row) => ({ ...row, name: normalizeInsuranceName(row.name) }))
          .filter((row) => row.name.length > 0),
      );
      set({ availableInsurances, insurancesLoaded: true });
    } catch (error) {
      console.error('Error fetching insurances:', error);
    }
  },

  toggleFavorite: async (documentId: string, userId: number) => {
    try {
      const res = await fetch(`${baseUrl}/therapy-providers/${documentId}/toggle-favorite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!res.ok) throw new Error('Failed to toggle favorite');

      const { data } = await res.json();

      set((state) => ({
        providers: state.providers.map(p => {
          if (p.documentId !== documentId) return p;
          const existing = p.favorited_by || [];
          const updatedFavoritedBy = data.favorited
            ? existing.some(u => u.id === userId) ? existing : [...existing, { id: userId }]
            : existing.filter(u => u.id !== userId);
          return {
            ...p,
            favorited_by: updatedFavoritedBy,
            favoriteCount: data.favoriteCount,
          };
        }),
      }));

      return { success: true, favorited: data.favorited };
    } catch (err) {
      console.error('Error toggling favorite:', err);
      const message = err instanceof Error ? err.message : 'Failed to toggle favorite';
      return { success: false, error: message };
    }
  },

  syncMyFavorites: async (userId: number) => {
    try {
      const res = await fetch(`${baseUrl}/therapy-providers/my-favorites`);
      if (!res.ok) return;
      const { data: favoritedDocumentIds } = await res.json() as { data: string[] };
      const favSet = new Set(favoritedDocumentIds);
      set((state) => ({
        providers: state.providers.map(p => {
          const isFav = favSet.has(p.documentId);
          const existing = p.favorited_by || [];
          const alreadyMarked = existing.some(u => u.id === userId);
          if (isFav && alreadyMarked) return p;
          if (!isFav && !alreadyMarked) return p;
          const updatedFavoritedBy = isFav
            ? [...existing, { id: userId }]
            : existing.filter(u => u.id !== userId);
          return { ...p, favorited_by: updatedFavoritedBy };
        }),
      }));
    } catch (error) {
      console.error('src/store/providerStore.ts', error);
      // Non-fatal
    }
  },

  fetchFavoriteState: async (documentId: string, userId: number) => {
    try {
      const res = await fetch(`${baseUrl}/therapy-providers/${documentId}/favorite-state`);
      if (!res.ok) return;
      const { data } = await res.json();
      set((state) => ({
        providers: state.providers.map(p => {
          if (p.documentId !== documentId) return p;
          const existing = p.favorited_by || [];
          const updatedFavoritedBy = data.favorited
            ? existing.some(u => u.id === userId) ? existing : [...existing, { id: userId }]
            : existing.filter(u => u.id !== userId);
          return { ...p, favorited_by: updatedFavoritedBy };
        }),
      }));
    } catch (error) {
      console.error('src/store/providerStore.ts', error);
      // Non-fatal — UI falls back to existing store state
    }
  },

  hasUserFavorited: (documentId: string, userId: number) => {
    const provider = get().providers.find(p => p.documentId === documentId);
    return provider?.favorited_by?.some(u => u.id === userId) ?? false;
  },

  setSelectedCategory: (category: ProviderCategoryFilter) => {
    set({ selectedCategory: category });
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  getFilteredProviders: (userId?: number) => {
    const { providers, selectedCategory, searchQuery } = get();

    let filtered = [...providers];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => filterProviderByCategory(p, selectedCategory));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter((p) => providerMatchesQuery(p, searchQuery));
    }

    // Three-tier sort:
    //   0 — favorited by current user (always first regardless of accepting status)
    //   1 — accepting new patients
    //   2 — not accepting new patients
    // Within each tier, providers are sorted alphabetically by name.
    filtered.sort((a, b) => {
      const aFav = userId ? (a.favorited_by?.some(u => u.id === userId) ?? false) : false;
      const bFav = userId ? (b.favorited_by?.some(u => u.id === userId) ?? false) : false;
      const aPriority = aFav ? 0 : a.acceptingPatients ? 1 : 2;
      const bPriority = bFav ? 0 : b.acceptingPatients ? 1 : 2;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.providerName.localeCompare(b.providerName);
    });

    return filtered;
  },

  fetchProviders: async () => {
    try {
      set({ loading: true, error: null });

      const rawProviders = await fetchStrapiCollection<StrapiProviderItem>('therapy-providers', {
        filters: { deletedAt: { $null: true } },
        // List view also needs favorite state; detail/write paths don't.
        populate: { ...PROVIDER_DETAIL_POPULATE, favorited_by: true },
        sort: ['createdAt:desc'],
      });

      const providers: Provider[] = rawProviders.map((item) => mapApiProviderToStore(item));

      set({ providers, loading: false });
    } catch (err) {
      console.error('Error fetching providers:', err);
      set({ error: 'Failed to fetch providers', loading: false });
    }
  },

  createProvider: async (data, imageFile) => {
    try {
      set({ loading: true, error: null });

      // First upload image if provided
      let photoId: number | null = null;
      if (imageFile) {
        validateFileSize(imageFile);

        const formData = new FormData();
        formData.append('files', imageFile);

        const uploadRes = await fetch(`${baseUrl}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (uploadRes.ok) {
          const uploadedFiles = await uploadRes.json();
          if (uploadedFiles.length > 0) {
            photoId = uploadedFiles[0].id;
          }
        } else {
          throw new Error(await getUploadErrorMessage(uploadRes, 'Failed to upload provider photo'));
        }
      }

      // MWBI-803 — translate name arrays into the new relation payloads.
      // Lazy-fetch the canonical lists if the caller hasn't done so yet.
      await ensureProviderTaxonomyListsLoaded(get);
      // MWBI-844 — insurances can be created on the fly just before this call,
      // so refresh the list to pick up brand-new rows. Without this their names
      // would not resolve and the insurance relation would be dropped.
      if (data.insurance !== undefined) await get().fetchInsurances(true);
      const providerPayload = buildProviderRelationPayload(data, get);

      if (photoId) {
        providerPayload.data.photo = photoId;
      }

      // MWBI-852 — populate the response so the newly created card renders its
      // relations + photo immediately instead of after a refresh.
      const populateQuery = qs.stringify(
        { populate: PROVIDER_DETAIL_POPULATE },
        { encodeValuesOnly: true },
      );
      const res = await fetch(`${baseUrl}/therapy-providers?${populateQuery}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(providerPayload),
      });

      if (!res.ok) {
        throw new Error(await getUploadErrorMessage(res, 'Failed to create provider'));
      }

      const json = await res.json();
      const created = json.data;

      const provider = mapApiProviderToStore(created as StrapiProviderItem);

      set((state) => ({
        providers: [provider, ...state.providers],
        loading: false,
      }));

      return { success: true, provider };
    } catch (err: unknown) {
      console.error('Error creating provider:', err);
      const message = err instanceof Error ? err.message : 'Failed to create provider';
      set({ error: message, loading: false });
      return { success: false, error: message };
    }
  },

  updateProvider: async (documentId, data, imageFile) => {
    try {
      set({ loading: true, error: null });

      // First upload image if provided
      let photoId: number | null = null;
      if (imageFile) {
        validateFileSize(imageFile);

        const formData = new FormData();
        formData.append('files', imageFile);

        const uploadRes = await fetch(`${baseUrl}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (uploadRes.ok) {
          const uploadedFiles = await uploadRes.json();
          if (uploadedFiles.length > 0) {
            photoId = uploadedFiles[0].id;
          }
        } else {
          throw new Error(await getUploadErrorMessage(uploadRes, 'Failed to upload provider photo'));
        }
      }

      // MWBI-803 — same translator path as createProvider; for an update,
      // omitting a name array (undefined) leaves the relation untouched;
      // an empty array clears it.
      await ensureProviderTaxonomyListsLoaded(get);
      // MWBI-844 — pick up insurances created moments ago so their names
      // resolve instead of being silently dropped from the relation.
      if (data.insurance !== undefined) await get().fetchInsurances(true);
      const providerPayload = buildProviderRelationPayload(data, get);

      if (photoId) {
        providerPayload.data.photo = photoId;
      }

      // MWBI-844 — populate the relations on the write response so the mapper
      // derives the up-to-date insurance / specialty / language names and the
      // UI reflects the save immediately, without a page refresh.
      const populateQuery = qs.stringify(
        { populate: PROVIDER_DETAIL_POPULATE },
        { encodeValuesOnly: true },
      );
      const res = await fetch(`${baseUrl}/therapy-providers/${documentId}?${populateQuery}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(providerPayload),
      });

      if (!res.ok) {
        throw new Error(await getUploadErrorMessage(res, 'Failed to update provider'));
      }

      const json = await res.json();
      const updated = json.data;
      const existingProvider = get().providers.find((provider) => provider.documentId === documentId);

      const provider = mapApiProviderToStore(updated as StrapiProviderItem, existingProvider);

      set((state) => ({
        providers: state.providers.map((p) =>
          p.documentId === documentId ? provider : p
        ),
        loading: false,
      }));

      return { success: true, provider };
    } catch (err: unknown) {
      console.error('Error updating provider:', err);
      const message = err instanceof Error ? err.message : 'Failed to update provider';
      set({ error: message, loading: false });
      return { success: false, error: message };
    }
  },

  fetchProvider: async (documentId) => {
    try {
      const query = qs.stringify(
        { populate: PROVIDER_DETAIL_POPULATE },
        { encodeValuesOnly: true }
      );
      const res = await fetch(`${baseUrl}/therapy-providers/${documentId}?${query}`);
      if (!res.ok) return null;

      const json = await res.json();
      if (!json.data) return null;

      const existingProvider = get().providers.find((p) => p.documentId === documentId);
      const provider = mapApiProviderToStore(json.data as StrapiProviderItem, existingProvider);

      set((state) => ({
        providers: state.providers.some((p) => p.documentId === documentId)
          ? state.providers.map((p) => p.documentId === documentId ? provider : p)
          : [...state.providers, provider],
      }));

      return provider;
    } catch (error) {
      console.error('src/store/providerStore.ts', error);
      return null;
    }
  },

  deleteProvider: async (documentId) => {
    try {
      const res = await fetch(`${baseUrl}/therapy-providers/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            deletedAt: new Date().toISOString(),
          },
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.error?.message || 'Failed to delete provider');
      }

      set((state) => ({
        providers: state.providers.filter((p) => p.documentId !== documentId),
      }));

      return { success: true };
    } catch (err: unknown) {
      console.error('Error deleting provider:', err);
      const message = err instanceof Error ? err.message : 'Failed to delete provider';
      return { success: false, error: message };
    }
  },
}));

